import React, { useRef } from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

// React 18 ships `useId`, but this project's `@types/react` is still pinned at 17 (no `useId` in
// its declarations) — a per-instance counter via `useRef` gives the same "stable for this mounted
// instance, unique on the page" guarantee without depending on a hook the types don't know about.
let fieldRowIdSeq = 0
const useStableId = (): string => {
  const ref = useRef<string | null>(null)
  if (!ref.current) ref.current = `field-row-${++fieldRowIdSeq}`
  return ref.current
}

interface Props {
  label: string
  /** Plain content, or a render-prop that receives the id `FieldRow` generated so the control can
   * bind it (`<Input id={id} />`, `<SelectTrigger id={id} />`, `<Switch id={id} />`, …) and pair
   * with the `<label htmlFor>` below — screen readers and label clicks then reach the actual control. */
  children: React.ReactNode | ((id: string) => React.ReactNode)
  pending?: boolean
  error?: string | null
  className?: string
}

/**
 * One labelled control row inside the activity panel (Task 17, spec B §9.7): a fixed label column,
 * a spinner next to the control while its own field mutation is in flight, and an error line
 * underneath on failure. Shared by every section that renders a label/control pair — sections with
 * a bespoke row shape (e.g. `DependenciesSection`'s link rows) don't use it.
 */
const FieldRow: React.FC<Props> = ({ label, children, pending, error, className }) => {
  const id = useStableId()
  return (
    <div className={cn('grid grid-cols-[120px_1fr] items-center gap-2 py-1', className)}>
      <label htmlFor={id} className="text-xs text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex-1 min-w-0">{typeof children === 'function' ? children(id) : children}</div>
        {pending && <LoaderCircle className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />}
      </div>
      {error && <p className="col-start-2 text-xs text-destructive">{error}</p>}
    </div>
  )
}

export default FieldRow
